// React and Router
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";

// Firebase
import { auth } from "../../firebase.js";
import { onAuthStateChanged } from "firebase/auth";

// Components
import Header from "../shared/Header";

// Styles
import "../../css/StorageManagement.css";

const API_BASE = "https://bluebirddocumentationadmin.pythonanywhere.com";

function ReferralDashboard() {
    const { uid } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [referralInfo, setReferralInfo] = useState(null);
    const [promotions, setPromotions] = useState([]);
    const [redeemCode, setRedeemCode] = useState("");
    const [message, setMessage] = useState(null);
    const [codeCopied, setCodeCopied] = useState(false);
    const [processingRedeemCode, setProcessingRedeemCode] = useState(false);
    const [claimingPromoId, setClaimingPromoId] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [currentUserUid, setCurrentUserUid] = useState(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setIsLoggedIn(true);
                setCurrentUserUid(user.uid);
            } else {
                setIsLoggedIn(false);
                navigate("/login");
            }
        });
        return () => unsubscribe();
    }, [navigate]);

    const getHeaders = useCallback(async () => {
        const token = await auth.currentUser.getIdToken();
        return {
            "Content-Type": "application/json",
            "Authorization": `${token}`,
            "auth-token": `Bearer ${token}`,
            "requesting-user-uid": currentUserUid,
        };
    }, [currentUserUid]);

    const fetchReferralInfo = useCallback(async () => {
        try {
            const headers = await getHeaders();
            const response = await fetch(`${API_BASE}/users/${uid}/referral/`, {
                method: "GET",
                headers,
            });
            if (response.ok) {
                const data = await response.json();
                setReferralInfo(data);
            }
        } catch (error) {
            console.error("Error fetching referral info:", error);
        }
    }, [uid, getHeaders]);

    const fetchPromotions = useCallback(async () => {
        try {
            const headers = await getHeaders();
            const response = await fetch(`${API_BASE}/users/${uid}/storage/promotions/`, {
                method: "GET",
                headers,
            });
            if (response.ok) {
                const data = await response.json();
                setPromotions(data.promotions || []);
            }
        } catch (error) {
            console.error("Error fetching promotions:", error);
        }
    }, [uid, getHeaders]);

    useEffect(() => {
        const loadData = async () => {
            if (!isLoggedIn || !currentUserUid) return;
            setLoading(true);
            await Promise.all([fetchReferralInfo(), fetchPromotions()]);
            setLoading(false);
        };
        loadData();
    }, [isLoggedIn, currentUserUid, fetchReferralInfo, fetchPromotions]);

    const handleCopyCode = () => {
        if (referralInfo?.referral_code) {
            navigator.clipboard.writeText(referralInfo.referral_code).then(() => {
                setCodeCopied(true);
                setTimeout(() => setCodeCopied(false), 2000);
            });
        }
    };

    const handleShareTwitter = () => {
        const code = referralInfo?.referral_code;
        const text = encodeURIComponent(`Check out bluebird documentation! Use my referral code ${code} to get free bonus storage.`);
        window.open(`https://twitter.com/intent/tweet?text=${text}`, "_blank");
    };

    const handleShareEmail = () => {
        const code = referralInfo?.referral_code;
        const subject = encodeURIComponent("Check out Bluebird Documentation!");
        const body = encodeURIComponent(`Hey! I've been using Bluebird Documentation and thought you might like it too.\n\nUse my referral code: ${code}\n\nYou'll get bonus storage when you sign up!`);
        window.open(`mailto:?subject=${subject}&body=${body}`, "_self");
    };

    const handleRedeem = async (e) => {
        e.preventDefault();
        if (!redeemCode.trim()) return;

        setProcessingRedeemCode(true);
        setMessage(null);

        try {
            const headers = await getHeaders();
            const response = await fetch(`${API_BASE}/users/${uid}/referral/redeem/`, {
                method: "POST",
                headers,
                body: JSON.stringify({ code: redeemCode.trim() }),
            });

            const data = await response.json();
            if (response.ok) {
                setMessage({ type: "success", text: data.message || "Referral code redeemed successfully!" });
                setRedeemCode("");
                fetchReferralInfo();
            } else {
                setMessage({ type: "error", text: data.error || "Failed to redeem code" });
            }
        } catch (error) {
            console.error("Error redeeming code:", error);
            setMessage({ type: "error", text: "An error occurred. Please try again." });
        } finally {
            setProcessingRedeemCode(false);
        }
    };

    const handleClaimPromotion = async (promotionId) => {
        setClaimingPromoId(promotionId);
        setMessage(null);

        try {
            const headers = await getHeaders();
            const response = await fetch(`${API_BASE}/users/${uid}/storage/promotions/${promotionId}/claim/`, {
                method: "POST",
                headers,
            });

            const data = await response.json();
            if (response.ok) {
                setMessage({ type: "success", text: data.message || "Promotion claimed successfully!" });
                fetchPromotions();
                fetchReferralInfo();
            } else {
                setMessage({ type: "error", text: data.error || "Failed to claim promotion" });
            }
        } catch (error) {
            console.error("Error claiming promotion:", error);
            setMessage({ type: "error", text: "An error occurred. Please try again." });
        } finally {
            setClaimingPromoId(null);
        }
    };

    const formatMB = (mb) => {
        if (!mb) return "0 MB";
        if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
        return `${mb} MB`;
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="ReferralDashboard__loading">
                    <p>Loading referral dashboard...</p>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="ReferralDashboard__title-section">
                <h1>Referrals & Promotions</h1>
                <p style={{ marginTop: "8px", opacity: 0.8 }}>Earn bonus storage by inviting friends and claiming promotions</p>
            </div>
            <div className="ReferralDashboard__main-content">
                <button className="ReferralDashboard__navigation-buttons" onClick={() => navigate(`/account/${uid}/storage`)}>
                    <i className="fas fa-arrow-left"></i> Back to Storage
                </button>

                {message && (
                    <div className={`ReferralDashboard__message ReferralDashboard__message--${message.type}`}>
                        {message.text}
                    </div>
                )}

                {/* Referral Code Section */}
                {referralInfo && (
                    <div className="ReferralDashboard__code-card">
                        <h2>Your Referral Code</h2>
                        <p style={{ marginBottom: "16px", opacity: 0.8 }}>Share your code with friends — you both earn bonus storage!</p>

                        <div className="ReferralDashboard__code-display">
                            <span className="ReferralDashboard__code-text">{referralInfo.referral_code}</span>
                            <button
                                className="ReferralDashboard__copy-button"
                                onClick={handleCopyCode}
                                title="Copy to clipboard"
                            >
                                {codeCopied ? (
                                    <i className="fas fa-check"></i>
                                ) : (
                                    <i className="fas fa-copy"></i>
                                )}
                            </button>
                        </div>

                        <div className="ReferralDashboard__share-section">
                            <button className="ReferralDashboard__share-button" onClick={handleShareTwitter}>
                                <i className="fab fa-twitter"></i> Share on X
                            </button>
                            <button className="ReferralDashboard__share-button" onClick={handleShareEmail}>
                                <i className="fas fa-envelope"></i> Share via Email
                            </button>
                        </div>

                        <div className="ReferralDashboard__stats-row">
                            <div className="ReferralDashboard__stat">
                                <span className="ReferralDashboard__stat-value">{referralInfo.total_referrals || 0}</span>
                                <span className="ReferralDashboard__stat-label">Referrals</span>
                            </div>
                            <div className="ReferralDashboard__stat">
                                <span className="ReferralDashboard__stat-value">{referralInfo.remaining_uses ?? "∞"}</span>
                                <span className="ReferralDashboard__stat-label">Remaining Uses</span>
                            </div>
                            <div className="ReferralDashboard__stat">
                                <span className="ReferralDashboard__stat-value">{formatMB(referralInfo.bonus_mb_referrer)}</span>
                                <span className="ReferralDashboard__stat-label">You Earn</span>
                            </div>
                            <div className="ReferralDashboard__stat">
                                <span className="ReferralDashboard__stat-value">{formatMB(referralInfo.bonus_mb_referee)}</span>
                                <span className="ReferralDashboard__stat-label">Friend Earns</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Redeem a Code Section */}
                <div className="ReferralDashboard__redeem-card">
                    <h2>Redeem a Referral Code</h2>
                    <p style={{ marginBottom: "16px", opacity: 0.8 }}>Have a referral code from a friend? Enter it below to earn bonus storage.</p>
                    <form className="ReferralDashboard__redeem-form" onSubmit={handleRedeem}>
                        <input
                            className="ReferralDashboard__redeem-input"
                            type="text"
                            value={redeemCode}
                            onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                            placeholder="Enter referral code (e.g. BBD-XXXXXXXX)"
                            maxLength={20}
                        />
                        <button
                            className="ReferralDashboard__redeem-button"
                            type="submit"
                            disabled={processingRedeemCode || !redeemCode.trim()}
                        >
                            {processingRedeemCode ? "Redeeming..." : "Redeem"}
                        </button>
                    </form>
                </div>

                {/* Available Promotions Section */}
                {promotions.length > 0 && (
                    <div className="ReferralDashboard__promotions-card">
                        <h2>Available Promotions</h2>
                        <p style={{ marginBottom: "16px", opacity: 0.8 }}>Claim available promotions to earn extra storage.</p>
                        <ul className="ReferralDashboard__promotions-list">
                            {promotions.map((promo) => (
                                <li key={promo.id} className="ReferralDashboard__promotion-item">
                                    <div className="ReferralDashboard__promotion-info">
                                        <strong>{promo.name}</strong>
                                        <span className="ReferralDashboard__promotion-bonus">
                                            +{formatMB(promo.bonus_mb)}
                                        </span>
                                    </div>
                                    {promo.description && (
                                        <p className="ReferralDashboard__promotion-description">{promo.description}</p>
                                    )}
                                    {promo.ends_at && (
                                        <p style={{ fontSize: "0.8rem", opacity: 0.6, marginBottom: "8px" }}>
                                            Expires: {new Date(promo.ends_at).toLocaleDateString()}
                                        </p>
                                    )}
                                    <button
                                        className="ReferralDashboard__promotion-claim"
                                        onClick={() => handleClaimPromotion(promo.id)}
                                        disabled={claimingPromoId === promo.id}
                                    >
                                        {claimingPromoId === promo.id ? "Claiming..." : "Claim"}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </>
    );
}

export default ReferralDashboard;
